import { useQuery } from '@hooks/reactQuery';

import {
	getAlarmOranAlarmIds,
	getAlarmOranAlarmIdsActive,
	getAlarmOranApplicabilities,
	getAlarmOranApplicabilitiesActive,
	getAlarmOranGroups,
	getAlarmOranGroupsActive,
	getAlarmOranModuleNames,
	getAlarmOranModuleNamesActive,
	getAlarmOranSeverities,
	getAlarmOranSeveritiesActive,
	getAlarmOranStatuses,
	getOranAcknowledgedBy,
	getOranAcknowledgedByForAlarmsActive,
	getOranClearedBy,
	getOranMiddlewaresForAlarms,
	getOranMiddlewaresForAlarmsActive,
	getOranNetworkElementsForAlarms,
	getOranNetworkElementsForAlarmsActive,
} from '@api/oranAlarms';

import { extractQueryParams } from '@utils/common';
import { mapDropdownOptions, optionsFormatted, optionsFormattedBySystemId } from '@utils/formatter';

import { IAlarmByVendorFilter, IAlarmFilter } from '@interfaces/alarms/alarm';
import { TNameTitle } from '@interfaces/common/common';
import { TSelectOptionType } from '@components/common/Select/Select.types';

import { EUserRoles } from '@enums/common';

import { booleanOptions, refetchOptions } from '@constants/common';
import {
	ALARM_APPLICABILITIES_ORAN,
	ALARM_GROUPS_ORAN,
	ALARM_IDS_ORAN,
	ALARM_MODULE_NAMES_ORAN,
	ALARM_SEVERITIES_ORAN,
	ALARM_STATUSES_ORAN,
	ORAN_ALARM_ACKNOWLEDGED_BY,
	ORAN_ALARM_CLEARED_BY,
	ORAN_MIDDLEWARES,
	ORAN_NETWORK_ELEMENTS,
} from '@constants/queryKeys';

interface IUseQueryOranCompliantActiveHistoryFiltersProps {
	isActiveTab: boolean;
}

export const useQueryOranCompliantActiveHistoryFilters = ({
	isActiveTab,
}: IUseQueryOranCompliantActiveHistoryFiltersProps): IAlarmByVendorFilter => {
	const {
		data: groups,
		isInitialLoading: isLoadingGroups,
	} = useQuery<Array<TNameTitle>, TSelectOptionType<string>[]>({
		queryKey: [ALARM_GROUPS_ORAN, { vendor: undefined }, isActiveTab],
		queryFn: isActiveTab ? extractQueryParams(getAlarmOranGroupsActive) : getAlarmOranGroups,
		select: (groups) => optionsFormatted<TNameTitle>(groups),
		allowedRoles: [EUserRoles.networkOperatorAlarmManager],
		...refetchOptions,
	});

	const {
		data: moduleNames,
		isInitialLoading: isLoadingModuleNames,
	} = useQuery<Array<TNameTitle>, TSelectOptionType<string>[]>({
		queryKey: [ALARM_MODULE_NAMES_ORAN, { vendor: undefined }, isActiveTab],
		queryFn: isActiveTab ? extractQueryParams(getAlarmOranModuleNamesActive) : getAlarmOranModuleNames,
		select: (groups) => optionsFormatted<TNameTitle>(groups),
		allowedRoles: [EUserRoles.networkOperatorAlarmManager],
		...refetchOptions,
	});

	const {
		data: severities,
		isInitialLoading: isLoadingSeverities,
	} = useQuery<Array<TNameTitle>, TSelectOptionType<string>[]>({
		queryKey: [ALARM_SEVERITIES_ORAN, { vendor: undefined }, isActiveTab],
		queryFn: isActiveTab ? extractQueryParams(getAlarmOranSeveritiesActive) : getAlarmOranSeverities,
		select: (groups) => optionsFormatted<TNameTitle>(groups),
		allowedRoles: [EUserRoles.networkOperatorAlarmManager],
		...refetchOptions,
	});

	const {
		data: statuses,
		isInitialLoading: isLoadingStatuses,
	} = useQuery<Array<TNameTitle>, TSelectOptionType<string>[]>({
		queryKey: [ALARM_STATUSES_ORAN],
		queryFn: getAlarmOranStatuses,
		select: (groups) => optionsFormatted<TNameTitle>(groups),
		allowedRoles: [EUserRoles.networkOperatorAlarmManager],
		...refetchOptions,
	});

	const {
		data: oranAlarmIds,
		isInitialLoading: isLoadingOranAlarmIds,
	} = useQuery<Array<TNameTitle>, TSelectOptionType<string>[]>({
		queryKey: [ALARM_IDS_ORAN, { vendor: undefined }, isActiveTab],
		queryFn: isActiveTab ? extractQueryParams(getAlarmOranAlarmIdsActive) : getAlarmOranAlarmIds,
		select: (alarmIds) => optionsFormatted<TNameTitle>(alarmIds),
		allowedRoles: [EUserRoles.networkOperatorAlarmManager],
		...refetchOptions,
	});

	const {
		data: applicabilities,
		isInitialLoading: isLoadingApplicabilities,
	} = useQuery<Array<TNameTitle>, TSelectOptionType<string>[]>({
		queryKey: [ALARM_APPLICABILITIES_ORAN, { vendor: undefined }, isActiveTab],
		queryFn: isActiveTab ? extractQueryParams(getAlarmOranApplicabilitiesActive) : getAlarmOranApplicabilities,
		select: (groups) => optionsFormatted<TNameTitle>(groups),
		allowedRoles: [EUserRoles.networkOperatorAlarmManager],
		...refetchOptions,
	});

	const {
		data: middlewares,
		isInitialLoading: isLoadingMiddlewares,
	} = useQuery({
		queryKey: [ORAN_MIDDLEWARES, { vendor: undefined }, isActiveTab],
		queryFn: isActiveTab ? extractQueryParams(getOranMiddlewaresForAlarmsActive) : getOranMiddlewaresForAlarms,
		allowedRoles: [EUserRoles.networkOperatorAlarmManager],
		select: (data) => optionsFormattedBySystemId(data),
		...refetchOptions,
	});

	const {
		data: networkElements,
		isInitialLoading: isLoadingNetworkElements,
	} = useQuery({
		queryKey: [ORAN_NETWORK_ELEMENTS, {	vendor: undefined }, isActiveTab],
		queryFn: isActiveTab
			? extractQueryParams(getOranNetworkElementsForAlarmsActive)
			: getOranNetworkElementsForAlarms,
		allowedRoles: [EUserRoles.networkOperatorAlarmManager],
		select: (data) => mapDropdownOptions({
			items: data,
			valueKey: 'id',
			labelKey: 'name',
			additionalMappings: {
				name: 'id',
			},
		}),
		...refetchOptions,
	});

	const {
		data: clearedByList,
		isInitialLoading: isLoadingClearedByList,
	} = useQuery({
		queryKey: [ORAN_ALARM_CLEARED_BY],
		queryFn: getOranClearedBy,
		allowedRoles: [EUserRoles.networkOperatorAlarmManager],
		select: (data) => mapDropdownOptions({
			items: data,
			valueKey: 'clearedType.name',
			labelKey: 'clearedByTitle',
			additionalMappings: {
				userId: 'userId',
				name: 'clearedType.name',
			},
		}),
		...refetchOptions,
	});

	const {
		data: acknowledgedByList,
		isInitialLoading: isLoadingAcknowledgedByList,
	} = useQuery({
		queryKey: [ORAN_ALARM_ACKNOWLEDGED_BY, { vendor: undefined }, isActiveTab],
		queryFn: isActiveTab ? extractQueryParams(getOranAcknowledgedByForAlarmsActive) : getOranAcknowledgedBy,
		allowedRoles: [EUserRoles.networkOperatorAlarmManager],
		select: (data) => mapDropdownOptions({
			items: data,
			valueKey: 'id',
			labelKey: 'fullName',
			additionalMappings: {
				name: 'id',
			},
		}),
		...refetchOptions,
	});

	const commonFilters: IAlarmFilter[] = [
		{
			name: 'alarmId',
			placeholder: 'Alarm ID',
			data: oranAlarmIds || [],
		},
		{
			name: 'severity',
			placeholder: 'Severity',
			data: severities || [],
		},
		{
			name: 'group',
			placeholder: 'Group',
			data: groups || [],
		},
		{
			name: 'applicability',
			placeholder: 'Applicability',
			data: applicabilities || [],
		},
		{
			name: 'moduleName',
			placeholder: 'Module',
			data: moduleNames || [],
		},
		{
			name: 'networkElementId',
			placeholder: 'Network Element',
			data: networkElements || [],
		},
		{
			name: 'middlewareId',
			placeholder: 'Middleware',
			data: middlewares || [],
		},
		{
			name: 'isAcknowledged',
			placeholder: 'Acknowledged',
			data: booleanOptions,
		},
		{
			name: 'acknowledgeUserId',
			placeholder: 'Acknowledged by',
			data: acknowledgedByList || [],
		},
	];

	const historyFilters = [
		{
			name: 'status',
			placeholder: 'Status',
			data: statuses || [],
		},
		{
			name: 'clearedType',
			placeholder: 'Cleared by',
			data: clearedByList || [],
		},
	];

	const oranCompliantFilters = !isActiveTab
		? [...commonFilters, ...historyFilters]
		: commonFilters;

	return {
		filters: oranCompliantFilters,
		isLoading: (
			isLoadingGroups ||
			isLoadingModuleNames ||
			isLoadingSeverities ||
			isLoadingApplicabilities ||
			isLoadingStatuses ||
			isLoadingOranAlarmIds ||
			isLoadingMiddlewares ||
			isLoadingNetworkElements ||
			isLoadingClearedByList ||
			isLoadingAcknowledgedByList
		),
	};
};
